import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  Building2,
  Briefcase,
  UserCog,
  LogOut,
  Menu,
  X,
  Clock,
  History,
  ScanFace,
  Calendar,
  FileText,
  CheckSquare,
  Hourglass,
  CalendarClock,
  Wallet,
  Target,
  UserPlus,
  FolderOpen,
  Receipt,
  ClipboardList,
  ScanLine,
  ShieldCheck,
  MapPin,
  FileClock,
  HelpCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { cn } from '../lib/utils';

const Sidebar = ({ isOpen, onClose }) => {
  const { user, isHR, isAdmin, isManager, isStaff } = useAuth();

  // Staff only sees limited menu for PWA experience
  const navItems = [
    // Staff Features (PWA focused)
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', show: true },
    { to: '/attendance', icon: Clock, label: 'Absensi', show: true },
    { to: '/attendance/history', icon: History, label: 'Riwayat Absensi', show: true },
    { to: '/face-registration', icon: ScanFace, label: 'Registrasi Wajah', show: true },
    { to: '/work-reports', icon: ClipboardList, label: 'Laporan Kerja', show: true },
    { to: '/patrol', icon: ScanLine, label: 'Patroli Keamanan', show: true },
    { to: '/leave', icon: FileText, label: 'Pengajuan Cuti', show: true },
    { to: '/overtime', icon: Hourglass, label: 'Lembur', show: false }, // hidden: non-attendance feature
    { to: '/calendar', icon: Calendar, label: 'Kalender', show: false }, // hidden: non-attendance feature
    
    // Manager Features
    { to: '/approvals', icon: CheckSquare, label: 'Persetujuan', show: isManager || isHR || isAdmin },
    
    // HR Features
    { to: '/shifts', icon: CalendarClock, label: 'Manajemen Shift', show: isHR || isAdmin },
    { to: '/security-posts', icon: ShieldCheck, label: 'Pos Keamanan', show: isHR || isAdmin },
    { to: '/payroll', icon: Wallet, label: 'Payroll', show: false }, // hidden: non-attendance feature
    { to: '/performance', icon: Target, label: 'Performance', show: false }, // hidden: non-attendance feature
    { to: '/recruitment', icon: UserPlus, label: 'Recruitment', show: isHR || isAdmin },
    { to: '/documents', icon: FolderOpen, label: 'Dokumen', show: false }, // hidden: non-attendance feature
    
    // Admin Features
    { to: '/employees', icon: Users, label: 'Karyawan', show: isManager || isHR || isAdmin },
    { to: '/departments', icon: Building2, label: 'Departemen', show: isHR || isAdmin },
    { to: '/positions', icon: Briefcase, label: 'Posisi', show: isHR || isAdmin },
    { to: '/sites', icon: MapPin, label: 'Site', show: isHR || isAdmin },
    { to: '/kontrak', icon: FileClock, label: 'Kontrak', show: isHR || isAdmin },
    { to: '/users', icon: UserCog, label: 'User Management', show: isAdmin },
    
    // Staff specific - Slip Gaji (my payslip)
    { to: '/my-payslip', icon: Receipt, label: 'Slip Gaji Saya', show: false }, // hidden: non-attendance feature

    // Help
    { to: '/panduan', icon: HelpCircle, label: 'Panduan', show: true },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-screen w-64 bg-white border-r border-border flex flex-col transition-transform duration-300 lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
              <img src="/logo192.png" alt="SILSA" className="w-full h-full object-cover" />
            </div>
            <span className="text-xl font-bold tracking-tight font-['Manrope'] text-foreground">
              SILSA
            </span>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.filter(item => item.show).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User info at bottom */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-primary/10 text-primary text-sm">
                {user?.nama_lengkap?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.nama_lengkap || 'User'}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role || 'staff'}</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="h-16 bg-white border-b border-border flex items-center justify-between px-4 lg:px-6">
      <button
        onClick={onMenuClick}
        className="lg:hidden text-muted-foreground hover:text-foreground"
      >
        <Menu className="w-6 h-6" />
      </button>

      <div className="flex-1" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-2">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary/10 text-primary text-sm">
                {user?.nama_lengkap?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <span className="hidden sm:block text-sm font-medium">
              {user?.nama_lengkap || 'User'}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div>
              <p className="font-medium">{user?.nama_lengkap || 'User'}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/profile')}>
            Profil Saya
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-destructive">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
};

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-layout">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:pl-64">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        
        <main className="p-4 lg:p-6">
          {children}
        </main>

        {/* Footer */}
        <footer className="p-4 text-center text-xs text-muted-foreground border-t border-border bg-white">
          Powered by PT Lintas Sinergi Andalan
        </footer>
      </div>
    </div>
  );
};

export default DashboardLayout;
